import { defineStore } from 'pinia'
import { db } from '@/firebaseConfig'
import { doc, getDoc } from 'firebase/firestore'
import { TOURNAMENT_SETTINGS_DB, TOURNAMENT_PLAYERS_DB, TOURNAMENT_TIMERS_DB } from '@/constants/constants'
import { mergeDisplayConfig } from '@/utils/displayConfig'

export const useTournamentStore = defineStore('tournament', {
  state: () => ({
    tournamentName: null,
    players: [],
    bustedPlayers: [],
    levels: [],
    timerState: {
      timeLeft: null,
      level: 1,
      normalLevelCount: 1,
      timerRunning: false,
      endTime: null,
      countdownDuration: null,
      tournamentSecondsElapsed: 0,
      elapsedSecondsBeforeCurrentLevel: 0
    },
    settings: {
      buyinAmount: 0,
      rebuyAmount: 0,
      addonAmount: 0,
      rebuyAllowed: false,
      rebuyEndLevel: null,
      addonAllowed: false,
      buyinChips: 1000,
      addonChips: 1000,
      maxTables: 0,
      maxPlayersPerTable: 10,
      payoutTables: [],
      trackPlayersByName: false,
      leagueId: null,
      displayConfig: mergeDisplayConfig(),
      announcement: null
    }
  }),

  actions: {
    setTournamentName(name) {
      this.tournamentName = name
    },

    setTournamentSecondsElapsed(seconds) {
      this.timerState.tournamentSecondsElapsed = seconds
    },

    setElapsedSecondsBeforeCurrentLevel(seconds) {
      this.timerState.elapsedSecondsBeforeCurrentLevel = seconds
    },

    async loadTournamentData() {
      if (!this.tournamentName) return

      try {
        // Load tournament settings
        const settingsDoc = await getDoc(doc(db, TOURNAMENT_SETTINGS_DB, this.tournamentName))
        if (settingsDoc.exists()) {
          const data = settingsDoc.data()
          this.levels = data.levels || []
          this.settings = {
            buyinAmount: data.buyinAmount || 0,
            rebuyAmount: data.rebuyAmount || 0,
            addonAmount: data.addonAmount || 0,
            rebuyAllowed: data.rebuyAllowed || false,
            rebuyEndLevel: data.rebuyEndLevel || null,
            addonAllowed: data.addonAllowed || false,
            buyinChips: data.buyinChips || 1000,
            addonChips: data.addonChips || 1000,
            maxTables: typeof data.maxTables === 'number' ? data.maxTables : 0,
            maxPlayersPerTable: typeof data.maxPlayersPerTable === 'number' ? data.maxPlayersPerTable : 10,
            payoutTables: Array.isArray(data.payoutTables) ? data.payoutTables : [],
            trackPlayersByName: typeof data.trackPlayersByName === 'boolean' ? data.trackPlayersByName : false,
            leagueId: data.leagueId || null,
            displayConfig: mergeDisplayConfig(data.displayConfig),
            announcement: data.announcement?.text ? { text: data.announcement.text } : null
          }
        }

        // Load players data
        const playersDoc = await getDoc(doc(db, TOURNAMENT_PLAYERS_DB, this.tournamentName))
        if (playersDoc.exists()) {
          const data = playersDoc.data()
          this.players = data.players || []
          this.bustedPlayers = data.bustedPlayers || []
        }

        // Load timer state
        const timerDoc = await getDoc(doc(db, TOURNAMENT_TIMERS_DB, this.tournamentName))
        if (timerDoc.exists()) {
          const data = timerDoc.data()
          this.timerState = {
            timeLeft: data.timeLeft,
            level: data.level || 1,
            normalLevelCount: data.normalLevelCount || 1,
            timerRunning: data.timerRunning || false,
            endTime: data.endTime,
            countdownDuration: data.countdownDuration,
            tournamentSecondsElapsed: typeof data.tournamentSecondsElapsed === 'number' ? data.tournamentSecondsElapsed : 0,
          elapsedSecondsBeforeCurrentLevel: typeof data.elapsedSecondsBeforeCurrentLevel === 'number' ? data.elapsedSecondsBeforeCurrentLevel : 0
          }
        }
      } catch (error) {
        console.error('Error loading tournament data:', error)
      }
    },

    clearTournamentData() {
      this.players = []
      this.bustedPlayers = []
      this.levels = []
      this.timerState = {
        timeLeft: null,
        level: 1,
        normalLevelCount: 1,
        timerRunning: false,
        endTime: null,
        countdownDuration: null,
        tournamentSecondsElapsed: 0,
        elapsedSecondsBeforeCurrentLevel: 0
      }
      this.settings = {
        buyinAmount: 0,
        rebuyAmount: 0,
        addonAmount: 0,
        rebuyAllowed: false,
        rebuyEndLevel: null,
        addonAllowed: false,
        buyinChips: 1000,
        addonChips: 1000,
        maxTables: 0,
        maxPlayersPerTable: 10,
        payoutTables: [],
        trackPlayersByName: false,
        leagueId: null,
        displayConfig: mergeDisplayConfig(),
        announcement: null
      }
    }
  }
})

